<template>
  <div class="card p-shadow-app">
    <h1>Produtos</h1>
    <Toast :style="{ width: $isMobile() ? '85vw' : '' }" />
    <ConfirmDialog></ConfirmDialog>
    <ProductForm
      :show="productForm.show"
      :product="productForm.product"
      v-on:cancel="hideForm"
      v-on:productAdded="productAdded"
      v-on:productUpdated="productUpdated"
    />
    <div style="height: 75vh">
      <DataTable
        :value="list"
        :selection.sync="selectedProducts"
        dataKey="id"
        :filters.sync="filters"
        filterDisplay="row"
        :loading="loading"
        class="p-datatable-sm"
        sortMode="single"
        responsiveLayout="scroll"
        :scrollable="true"
        scrollHeight="flex"
        :paginator="true"
        :rows="perPage"
        :rowsPerPageOptions="[10, 25, 50, 100, 500]"
        :lazy="true"
        :totalRecords="totalRecords"
        @filter="loadProducts"
        @sort="loadProducts"
        @page="loadProducts"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Mostrar de {first} a {last} de {totalRecords} dados"
      >
        <template #empty> Nada a mostrar. </template>
        <template #loading> A carregar. Por favor Aguarde... </template>
        <template #header>
          <div class="table-header">
            <Button
              icon="pi pi-plus"
              @click="addNew"
              v-if="can('createUpdateParts')"
            />
            <Button
              icon="pi pi-trash"
              class="p-button-danger p-ml-1"
              :label="'Eliminar (' + selectedProducts.length + ')'"
              v-tooltip="'Eliminar Produtos Selecionados'"
              @click="confirmBulkDelete"
              :disabled="bulkDeleteBatchActive"
              v-if="can('createUpdateParts') && selectedProducts.length > 0"
            />
            <span
              v-if="bulkDeleteBatch.status"
              class="bulk-delete-status p-ml-2"
              :class="bulkDeleteBatchStatusClass"
              v-tooltip="'Clique para atualizar o estado da eliminação'"
              @click="loadBulkDeleteBatchStatus"
            >
              <i
                class="pi p-mr-1"
                :class="bulkDeleteBatch.status.cancelled ? 'pi-ban' : (bulkDeleteBatch.status.finished ? (bulkDeleteBatch.status.failed_jobs > 0 ? 'pi-exclamation-triangle' : 'pi-check-circle') : 'pi-spin pi-spinner')"
              ></i>
              {{ bulkDeleteBatchStatusLabel }}
            </span>
            <Button
              v-if="bulkDeleteBatchActive && can('createUpdateParts')"
              icon="pi pi-stop-circle"
              class="p-button-danger p-button-sm p-ml-1"
              v-tooltip="'Cancelar eliminação em lote'"
              :loading="bulkDeleteBatch.cancelling"
              @click="confirmCancelBulkDeleteBatch"
            />
          </div>
        </template>
        <Column selectionMode="multiple" :headerStyle="{ width: '3em' }"></Column>
        <Column
          header="Ações"
          :headerStyle="{ width: '5rem' }"
          :bodyStyle="{ 'text-align': 'center' }"
        >
          <template #body="slotProps">
            <Button
              icon="pi pi-trash"
              class="p-button-rounded p-button-text p-button-danger"
              v-tooltip.top="'Eliminar Produto'"
              @click="confirmDelete(slotProps.data)"
              v-if="can('createUpdateParts')"
            />
          </template>
        </Column>
        <Column
          field="reference"
          header="Referência"
          sortable
          filterField="reference"
          :headerStyle="{ 'min-width': '10%', 'max-width': '10%' }"
          :filterHeaderStyle="{ 'min-width': '10%', 'max-width': '10%' }"
          :bodyStyle="{ 'min-width': '10%', 'max-width': '10%' }"
        >
          <template #body="slotProps">
            <span
              class="clickable-item"
              @click="edit(slotProps.data)"
              v-if="can('createUpdateParts')"
            >
              {{ slotProps.data.reference ?? "_______" }}
            </span>
            <span v-else>
              {{ slotProps.data.reference ?? "" }}
            </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput
              v-model="filterModel.value"
              :callback="filterCallback"
            />
          </template>
        </Column>
        <Column
          field="family_id"
          header="Família"
          sortable
          filterField="family"
          :headerStyle="{ 'min-width': '10%', 'max-width': '12%' }"
          :filterHeaderStyle="{ 'min-width': '10%', 'max-width': '12%' }"
          :bodyStyle="{ 'min-width': '10%', 'max-width': '12%' }"
        >
          <template #body="slotProps">
            <span
              class="clickable-item"
              @click="edit(slotProps.data)"
              v-if="can('createUpdateParts')"
            >
              {{
                slotProps.data.family ? slotProps.data.family.name : "_______"
              }}
            </span>
            <span v-else>
              {{ slotProps.data.family ? slotProps.data.family.name : "" }}
            </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput
              v-model="filterModel.value"
              :callback="filterCallback"
            />
          </template>
        </Column>
        <Column
          field="category"
          header="Categoria"
          sortable
          filterField="category"
          :headerStyle="{ 'min-width': '12%', 'max-width': '14%' }"
          :filterHeaderStyle="{ 'min-width': '12%', 'max-width': '14%' }"
          :bodyStyle="{ 'min-width': '12%', 'max-width': '14%' }"
        >
          <template #body="slotProps">
            <span
              class="clickable-item"
              @click="edit(slotProps.data)"
              v-if="can('createUpdateParts')"
            >
              {{ productCategories(slotProps.data) || "_______" }}
            </span>
            <span v-else>
              {{ productCategories(slotProps.data) }}
            </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput
              v-model="filterModel.value"
              :callback="filterCallback"
            />
          </template>
        </Column>
        <Column
          field="long_description"
          filterField="long_description"
          header="Descrição"
          sortable
          :headerStyle="{ 'min-width': '18%', 'max-width': '18%' }"
          :filterHeaderStyle="{ 'min-width': '18%', 'max-width': '18%' }"
          :bodyStyle="{ 'min-width': '18%', 'max-width': '18%' }"
        >
          <template #body="slotProps">
            <span
              class="clickable-item"
              @click="edit(slotProps.data)"
              v-if="can('createUpdateParts')"
            >
              {{ slotProps.data.long_description ?? "_______" }}
            </span>
            <span v-else>
              {{ slotProps.data.long_description ?? "" }}
            </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput
              v-model="filterModel.value"
              :callback="filterCallback"
            />
          </template>
        </Column>
        <Column
          field="cost_price"
          filterField="cost_price"
          header="Preço de Custo"
          sortable
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #body="slotProps">
            <span
              class="clickable-item"
              @click="edit(slotProps.data)"
              v-if="can('createUpdateParts')"
            >
              {{ slotProps.data.cost_price ?? "_______" }} €
            </span>
            <span v-else> {{ slotProps.data.cost_price ?? "" }} € </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput
              v-model="filterModel.value"
              :callback="filterCallback"
            />
          </template>
        </Column>
        <Column
          field="sale_price"
          filterField="sale_price"
          header="Preço de Venda"
          sortable
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #body="slotProps">
            <span
              class="clickable-item"
              @click="edit(slotProps.data)"
              v-if="can('createUpdateParts')"
            >
              {{ slotProps.data.sale_price ?? "_______" }} €
            </span>
            <span v-else> {{ slotProps.data.sale_price ?? "" }} € </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput
              v-model="filterModel.value"
              :callback="filterCallback"
            />
          </template>
        </Column>
        <Column
          field="stock"
          filterField="stock"
          header="Stock"
          sortable
          :headerStyle="{ 'min-width': '6%', 'max-width': '6%' }"
          :filterHeaderStyle="{ 'min-width': '6%', 'max-width': '6%' }"
          :bodyStyle="{ 'min-width': '6%', 'max-width': '6%' }"
        >
          <template #body="slotProps">
            <span
              class="clickable-item"
              @click="edit(slotProps.data)"
              v-if="can('createUpdateParts')"
            >
              {{ slotProps.data.stock ?? "_______" }}
            </span>
            <span v-else>
              {{ slotProps.data.stock ?? "" }}
            </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput
              v-model="filterModel.value"
              :callback="filterCallback"
            />
          </template>
        </Column>
        <Column
          field="remote_stock"
          filterField="remote_stock"
          header="Stock Remoto"
          sortable
          :headerStyle="{ 'min-width': '6%', 'max-width': '6%' }"
          :filterHeaderStyle="{ 'min-width': '6%', 'max-width': '6%' }"
          :bodyStyle="{ 'min-width': '6%', 'max-width': '6%' }"
        >
          <template #body="slotProps">
            <span
              class="clickable-item"
              @click="edit(slotProps.data)"
              v-if="can('createUpdateParts')"
            >
              {{ slotProps.data.remote_stock ?? "_______" }}
            </span>
            <span v-else>
              {{ slotProps.data.remote_stock ?? "" }}
            </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput
              v-model="filterModel.value"
              :callback="filterCallback"
            />
          </template>
        </Column>
        <Column
          field="weight"
          filterField="weight"
          header="Peso"
          sortable
          :headerStyle="{ 'min-width': '6%', 'max-width': '6%' }"
          :filterHeaderStyle="{ 'min-width': '6%', 'max-width': '6%' }"
          :bodyStyle="{ 'min-width': '6%', 'max-width': '6%' }"
        >
          <template #body="slotProps">
            <span
              class="clickable-item"
              @click="edit(slotProps.data)"
              v-if="can('createUpdateParts')"
            >
              {{ formatWeight(slotProps.data.weight) }}
            </span>
            <span v-else>
              {{ formatWeight(slotProps.data.weight) }}
            </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput
              v-model="filterModel.value"
              :callback="filterCallback"
            />
          </template>
        </Column>
        <Column
          field="last_price_update"
          filterField="last_price_update"
          header="Última Atualização de Preço"
          sortable
          :headerStyle="{ 'min-width': '10%', 'max-width': '10%' }"
          :filterHeaderStyle="{ 'min-width': '10%', 'max-width': '10%' }"
          :bodyStyle="{ 'min-width': '10%', 'max-width': '10%' }"
        >
          <template #body="slotProps">
            <span
              class="clickable-item"
              @click="edit(slotProps.data)"
              v-if="can('createUpdateParts')"
            >
              {{ slotProps.data.last_price_update ?? "_______" }}
            </span>
            <span v-else>
              {{ slotProps.data.last_price_update ?? "" }}
            </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput
              v-model="filterModel.value"
              :callback="filterCallback"
            />
          </template>
        </Column>
        <Column
          field="website_update"
          filterField="website_update"
          header="Última Atualização do Site"
          sortable
          :headerStyle="{ 'min-width': '10%', 'max-width': '10%' }"
          :filterHeaderStyle="{ 'min-width': '10%', 'max-width': '10%' }"
          :bodyStyle="{ 'min-width': '10%', 'max-width': '10%' }"
        >
          <template #body="slotProps">
            <span
              class="clickable-item"
              @click="edit(slotProps.data)"
              v-if="can('createUpdateParts')"
            >
              {{ slotProps.data.website_update ?? "_______" }}
            </span>
            <span v-else>
              {{ slotProps.data.website_update ?? "" }}
            </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput
              v-model="filterModel.value"
              :callback="filterCallback"
            />
          </template>
        </Column>
        <Column
          field="show_website"
          header="Mostar no Site"
          sortable
          filterField="show_website"
          :headerStyle="{ 'min-width': '10%', 'max-width': '10%' }"
          :filterHeaderStyle="{ 'min-width': '10%', 'max-width': '10%' }"
          :bodyStyle="{ 'min-width': '10%', 'max-width': '10%' }"
        >
          <template #body="slotProps">
            {{ slotProps.data.show_website == 1 ? "Sim" : "Não" }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <Dropdown
              v-model="filterModel.value"
              @input="filterCallback()"
              :options="[
                { value: 0, label: 'Não' },
                { value: 1, label: 'Sim' },
              ]"
              optionValue="value"
              optionLabel="label"
              class="p-column-filter"
              :showClear="true"
            >
            </Dropdown>
          </template>
        </Column>
        <Column
          field="active"
          header="Ativo"
          sortable
          filterField="active"
          :headerStyle="{ 'min-width': '10%', 'max-width': '10%' }"
          :filterHeaderStyle="{ 'min-width': '10%', 'max-width': '10%' }"
          :bodyStyle="{ 'min-width': '10%', 'max-width': '10%' }"
        >
          <template #body="slotProps">
            {{ slotProps.data.active == 1 ? "Sim" : "Não" }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <Dropdown
              v-model="filterModel.value"
              @input="filterCallback()"
              :options="[
                { value: 0, label: 'Não' },
                { value: 1, label: 'Sim' },
              ]"
              optionValue="value"
              optionLabel="label"
              class="p-column-filter"
              :showClear="true"
            >
            </Dropdown>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>
<script>
import "vue-loading-overlay/dist/vue-loading.css";
import sparsService from "../../services/spars.service.js";
import { FilterMatchMode } from "primevue/api";
import ProductForm from "../../components/products/ProductForm.vue";
import DebouncedFilterInput from "@/components/elements/DebouncedFilterInput.vue";

export default {
  name: "SparsProducts",
  components: { ProductForm, DebouncedFilterInput },
  data() {
    return {
      loading: false,
      list: [],
      selectedProducts: [],
      productFamily: [],
      totalRecords: 0,
      perPage: 25,
      currentPage: 1,
      productForm: {
        show: false,
        product: null,
      },
      filters: {
        reference: { value: null, matchMode: FilterMatchMode.CONTAINS },
        family: { value: null, matchMode: FilterMatchMode.CONTAINS },
        category: { value: null, matchMode: FilterMatchMode.CONTAINS },
        active: { value: null, matchMode: FilterMatchMode.EQUALS },
        show_website: { value: null, matchMode: FilterMatchMode.EQUALS },
        description: { value: null, matchMode: FilterMatchMode.CONTAINS },
        long_description: { value: null, matchMode: FilterMatchMode.CONTAINS },
        cost_price: { value: null, matchMode: FilterMatchMode.CONTAINS },
        sale_price: { value: null, matchMode: FilterMatchMode.CONTAINS },
        stock: { value: null, matchMode: FilterMatchMode.CONTAINS },
        remote_stock: { value: null, matchMode: FilterMatchMode.CONTAINS },
        weight: { value: null, matchMode: FilterMatchMode.CONTAINS },
        last_price_update: { value: null, matchMode: FilterMatchMode.CONTAINS },
        website_update: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
      bulkDeleteBatch: {
        id: null,
        status: null,
        pollTimer: null,
        inFlight: false,
        cancelling: false,
      },
      loadRequestId: 0,
      destroyed: false,
    };
  },
  computed: {
    bulkDeleteBatchActive() {
      return Boolean(
        this.bulkDeleteBatch.id &&
        (!this.bulkDeleteBatch.status ||
          (!this.bulkDeleteBatch.status.finished && !this.bulkDeleteBatch.status.cancelled))
      );
    },
    bulkDeleteBatchStatusClass() {
      const status = this.bulkDeleteBatch.status;
      if (!status) {
        return "";
      }
      if (status.cancelled) {
        return "bulk-delete-status-failed";
      }
      if (!status.finished) {
        return "bulk-delete-status-pending";
      }
      if (status.failed_jobs > 0) {
        return "bulk-delete-status-failed";
      }
      return "bulk-delete-status-completed";
    },
    bulkDeleteBatchStatusLabel() {
      const status = this.bulkDeleteBatch.status;
      if (!status) {
        return "";
      }
      if (status.cancelled) {
        return `Eliminação cancelada: ${status.processed_jobs}/${status.total_jobs} processados`;
      }
      if (!status.finished) {
        return `A eliminar: ${status.processed_jobs}/${status.total_jobs} (${status.progress}%)`;
      }
      if (status.failed_jobs > 0) {
        return `Eliminação: ${status.successful_jobs}/${status.total_jobs} (${status.failed_jobs} falharam)`;
      }
      return `Eliminação concluída: ${status.successful_jobs}/${status.total_jobs} produtos`;
    },
  },
  async created() {
    this.loading = true;
    await this.loadProducts();
    this.loading = false;
    this.restoreBulkDeleteBatch();
  },
  deactivated() {
    this.hideForm();
    document.body.classList.remove('p-overflow-hidden');
  },
  beforeDestroy() {
    this.destroyed = true;
    this.stopBulkDeleteBatchPolling();
    document.body.classList.remove('p-overflow-hidden');
  },
  methods: {
    productCategories(product) {
      if (!product || !product.equipments || product.equipments.length === 0) {
        return "";
      }
      const names = product.equipments
        .map(eq => eq.spares_category ? eq.spares_category.name : null)
        .filter(Boolean);

      const unique = Array.from(new Set(names));
      return unique.join(", ");
    },
    formatWeight(weight) {
      if (weight === null || weight === undefined) {
        return "";
      }

      return `${Number(weight).toLocaleString("pt-PT", {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })} kg`;
    },
    async loadProducts(event = {}) {
      const requestId = ++this.loadRequestId;
      this.loading = true;

      try {
        const page = event.page ? event.page + 1 : 1;
        const perPage = event.rows ? event.rows : this.perPage;

        const params = {
          page,
          per_page: perPage
        };

        for (let key in this.filters) {
          const filterValue = this.filters[key].value;
          if (filterValue !== null && filterValue !== '') {
            params[key] = filterValue;
          }
        }

        const response = await sparsService.getProducts(params);
        if (requestId !== this.loadRequestId || this.destroyed) {
          return;
        }

        this.list = response.data || [];
        this.totalRecords = response.total || 0;
        this.currentPage = response.current_page || 1;
        this.perPage = response.per_page || 25;
      } catch (error) {
        if (requestId !== this.loadRequestId || this.destroyed) {
          return;
        }
        console.error(error);
        this.$toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao carregar dados',
          life: 3000
        });
      } finally {
        if (requestId === this.loadRequestId && !this.destroyed) {
          this.loading = false;
        }
      }
    },
    hideForm() {
      this.productForm = {
        show: false,
        product: null,
      };
    },
    addNew() {
      return (this.productForm = {
        show: true,
        product: null,
      });
    },
    edit(data) {
      return (this.productForm = {
        show: true,
        product: data,
      });
    },
    async productAdded() {
      await this.loadProducts();
      return this.hideForm();
    },
    async productUpdated() {
      await this.loadProducts();
      return this.hideForm();
    },
    confirmDelete(product) {
      this.$confirm.require({
        header: "Eliminar Produto",
        message: `Tem a certeza que pretende eliminar o produto "${product.reference}"? O produto será removido dos equipamentos, do intranet e da loja.`,
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Sim, Eliminar",
        acceptIcon: "pi pi-trash",
        acceptClass: "p-button-danger p-button p-component",
        rejectLabel: "Cancelar",
        rejectIcon: "pi pi-times",
        rejectClass: "p-button-secondary p-button p-component",
        accept: async () => {
          this.loading = true;
          try {
            await sparsService.deleteProduct(product.id);
            this.selectedProducts = this.selectedProducts.filter(
              selected => selected.id !== product.id
            );
            await this.loadProducts({ page: this.currentPage - 1, rows: this.perPage });
            this.$toast.add({
              severity: "success",
              summary: "Sucesso",
              detail: "Produto eliminado com sucesso",
              life: 3000,
            });
          } catch (error) {
            const detail = error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : "Erro ao eliminar produto";
            this.$toast.add({
              severity: "error",
              summary: "Erro",
              detail,
              life: 4000,
            });
          } finally {
            this.loading = false;
          }
        },
      });
    },
    restoreBulkDeleteBatch() {
      const savedBatchId = localStorage.getItem("spars_product_bulk_delete_batch_id");
      if (savedBatchId) {
        this.bulkDeleteBatch.id = savedBatchId;
        this.loadBulkDeleteBatchStatus();
      }
    },
    stopBulkDeleteBatchPolling() {
      if (this.bulkDeleteBatch.pollTimer) {
        clearTimeout(this.bulkDeleteBatch.pollTimer);
        this.bulkDeleteBatch.pollTimer = null;
      }
    },
    confirmCancelBulkDeleteBatch() {
      if (!this.bulkDeleteBatchActive || this.bulkDeleteBatch.cancelling) {
        return;
      }
      this.$confirm.require({
        header: "Cancelar Eliminação",
        message: "Pretende cancelar os produtos que ainda não começaram a ser eliminados? O trabalho já em execução pode terminar.",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Sim, Cancelar",
        acceptClass: "p-button-danger p-button p-component",
        rejectLabel: "Manter",
        rejectClass: "p-button-secondary p-button p-component",
        accept: () => this.cancelBulkDeleteBatch(),
      });
    },
    async cancelBulkDeleteBatch() {
      this.bulkDeleteBatch.cancelling = true;
      try {
        await sparsService.cancelProductBulkDeleteBatch(this.bulkDeleteBatch.id);
        await this.loadBulkDeleteBatchStatus();
        this.$toast.add({
          severity: "info",
          summary: "Cancelamento solicitado",
          detail: "Os produtos ainda não iniciados não serão eliminados.",
          life: 4000,
        });
      } catch (error) {
        const detail = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : "Não foi possível cancelar a eliminação em lote.";
        this.$toast.add({ severity: "error", summary: "Erro", detail, life: 4000 });
      } finally {
        this.bulkDeleteBatch.cancelling = false;
      }
    },
    async loadBulkDeleteBatchStatus() {
      if (!this.bulkDeleteBatch.id || this.bulkDeleteBatch.inFlight || this.destroyed) {
        return;
      }
      const batchId = this.bulkDeleteBatch.id;
      this.bulkDeleteBatch.inFlight = true;
      try {
        const status = await sparsService.getProductBulkDeleteBatch(batchId);
        if (this.destroyed || this.bulkDeleteBatch.id !== batchId) {
          return;
        }
        this.bulkDeleteBatch.status = status;

        if (!status.finished && !status.cancelled) {
          this.stopBulkDeleteBatchPolling();
          this.bulkDeleteBatch.pollTimer = setTimeout(() => {
            this.loadBulkDeleteBatchStatus();
          }, 2500);
        } else {
          this.stopBulkDeleteBatchPolling();
          localStorage.removeItem("spars_product_bulk_delete_batch_id");
          await this.loadProducts({ page: this.currentPage - 1, rows: this.perPage });
          const failedIds = status.failed_entity_ids || [];
          this.selectedProducts = this.selectedProducts.filter(product => failedIds.includes(product.id));
          if (status.cancelled) {
            this.$toast.add({
              severity: "info",
              summary: "Eliminação cancelada",
              detail: `${status.processed_jobs} de ${status.total_jobs} trabalho(s) foram processados antes do cancelamento.`,
              life: 5000,
            });
          } else if (status.failed_jobs > 0) {
            this.$toast.add({
              severity: "warn",
              summary: "Eliminação em lote terminada",
              detail: `${status.successful_jobs} produto(s) eliminado(s), ${status.failed_jobs} falharam. IDs: ${failedIds.join(", ") || "indisponíveis"}.`,
              life: 5000,
            });
          } else {
            this.$toast.add({
              severity: "success",
              summary: "Eliminação concluída",
              detail: `${status.successful_jobs} produto(s) eliminado(s) com sucesso.`,
              life: 4000,
            });
          }
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.stopBulkDeleteBatchPolling();
          localStorage.removeItem("spars_product_bulk_delete_batch_id");
          this.bulkDeleteBatch.id = null;
          this.bulkDeleteBatch.status = null;
          return;
        }
        if (!this.destroyed && this.bulkDeleteBatch.id === batchId) {
          this.stopBulkDeleteBatchPolling();
          this.bulkDeleteBatch.pollTimer = setTimeout(() => {
            this.loadBulkDeleteBatchStatus();
          }, 5000);
        }
      } finally {
        this.bulkDeleteBatch.inFlight = false;
      }
    },
    confirmBulkDelete() {
      if (this.bulkDeleteBatchActive) {
        return;
      }
      const count = this.selectedProducts.length;
      this.$confirm.require({
        header: "Eliminar Produtos Selecionados",
        message: `Tem a certeza que pretende colocar ${count} produto(s) na fila de eliminação? Os produtos serão removidos dos equipamentos, do intranet e da loja em segundo plano.`,
        icon: "pi pi-exclamation-triangle",
        acceptLabel: `Sim, Eliminar (${count})`,
        acceptIcon: "pi pi-trash",
        acceptClass: "p-button-danger p-button p-component",
        rejectLabel: "Cancelar",
        rejectIcon: "pi pi-times",
        rejectClass: "p-button-secondary p-button p-component",
        accept: async () => {
          this.loading = true;
          try {
            const ids = this.selectedProducts.map(product => product.id);
            const result = await sparsService.bulkDeleteProducts(ids);

            this.bulkDeleteBatch.id = result.batch_id;
            localStorage.setItem("spars_product_bulk_delete_batch_id", result.batch_id);

            this.$toast.add({
              severity: "info",
              summary: "Em fila de processamento",
              detail: result.message || `${count} produtos colocados em fila para eliminação.`,
              life: 4000,
            });

            await this.loadBulkDeleteBatchStatus();
          } catch (error) {
            const detail = error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : "Erro ao iniciar eliminação em lote de produtos";
            this.$toast.add({
              severity: "error",
              summary: "Erro",
              detail,
              life: 4000,
            });
          } finally {
            this.loading = false;
          }
        },
      });
    },
  },
};
</script>

<style scoped>
.bulk-delete-status {
  display: inline-flex;
  align-items: center;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.35rem 0.65rem;
}

.bulk-delete-status-pending {
  background: #fff3cd;
  color: #856404;
}

.bulk-delete-status-completed {
  background: #d4edda;
  color: #155724;
}

.bulk-delete-status-failed {
  background: #f8d7da;
  color: #721c24;
}
</style>
